import React, { useEffect, useContext, useState }  from 'react'
import { View, StyleSheet, FlatList, SafeAreaView, ScrollView } from 'react-native'
import StyledTextInput from '../../components/StyledTextInput'
import { Text, Button, Spinner } from '@ui-kitten/components'
import { UserContext } from '../../global/UserProvider'
import { deleteRevision, saveNewRevision, updateRevision } from '../../global/Firebase/firebase'
import PlatformDependantSelect from '../../components/PlatformDependantSelect'
import * as Sentry from "@sentry/react-native"
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import { getDailyStatID } from '../../utils/timezoneFunctions'
import { parseDecimalAmount } from '../../utils/decimalInput'
import { allProductsByCategoryDictAtom, currencyFormatterAtom, productCategoriesDictAtom, productCategoriesListAtom, productUnitsDictAtom, storesDictAtom, userRoleAtom } from '../../global/AtomsProvider'
import HeaderIconButton from '../../components/HeaderIconButton'
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view'
import AdaptiveTableCard from '../../components/AdaptiveTableCard'
import { sanitizeForFirestore } from '../../global/Firebase/productSanitize'
import { isProductDocumentsMissingError } from '../../global/Firebase/productDocumentsMissingError'
import { usePostHog } from 'posthog-react-native'
import { APP_REVIEW_TRIGGERS, requestAppReviewAtMilestone } from '../../global/AppReview'
import { saveFailureText } from '../../global/Firebase/writeOutcome'

const correctionReasons = [
  { id: 'otherProductMixUp' },
  { id: 'forgotToCreateSupply' },
  { id: 'supplyError' },
  { id: 'forgotToWriteOff' },
  { id: 'forgotToMarkASale' },
  { id: 'wrongCount' },
  { id: 'other' },
]

export const addAdditionalFieldsToProductForRevision = (product, store) => {
  const stockValue = product.storeStock?.[store]
  return {...product,
    inStock: stockValue ? (product.type ? stockValue?.toFixed(3) : stockValue.toFixed()) : '0',
    inStockOld: stockValue ? (product.type ? stockValue?.toFixed(3) : stockValue.toFixed()) : '0',
  }
}

export default function RevisionDetailScreen({ navigation, route }) {
  const { t } = useTranslation()
  const [context, setContext] = useContext(UserContext)
  const db = context.db
  const productCategoriesDict = useAtomValue(productCategoriesDictAtom)
  const productCategoriesList = useAtomValue(productCategoriesListAtom)
  const [activeProductCategoriesList, setActiveProductCategoriesList] = useState(productCategoriesList)
  const allProductsByCategoryDict = useAtomValue(allProductsByCategoryDictAtom)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const productUnitsDict = useAtomValue(productUnitsDictAtom)
  const userRole = useAtomValue(userRoleAtom)
  const postHog = usePostHog()
  const { detail, preselectedProduct, preselectedStore, itemIndex, sectionIndex, addItemToData, editItemInData, removeItemFromData } = route.params
  const [store, setStore] = useState(detail != null ? detail.store : (preselectedStore || (Object.keys(storesDict) > 1 ? null : context.user.store.id)))
  const [data, setData] = useState(detail != null ? detail.items : (preselectedProduct != null ? [addAdditionalFieldsToProductForRevision(preselectedProduct, store)] : []))
  const [category, setCategory] = useState(detail != null ? detail.productCategory : (preselectedProduct != null ? productCategoriesDict[preselectedProduct.categoryID] : null))
  const [saveError, setSaveError] = useState('')
  const [successMessageVisible, setSuccessMessageVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const localizedCorrectionReasons = correctionReasons.map(item => ({...item, text: t('revisions.' + item.id)}))

  // Log revision detail data for E2E testing
  useEffect(() => {
    if (detail != null) {
      console.log('[REVISION_DETAIL_DATA]', JSON.stringify(sanitizeForFirestore({
        id: detail.id,
        store: detail.store,
        organizationId: context.user?.organizationId,
        // Scalar-map: the stored category carries a live Firestore ref (cyclical on iOS) —
        // stringifying it raw is the fatal Sentry 26B. e2e contract is { id, name } | null.
        productCategory: detail.productCategory
          ? { id: detail.productCategory.id, name: detail.productCategory.name }
          : null,
        amount: detail.amount,
        negativeAmount: detail.negativeAmount,
        positiveAmount: detail.positiveAmount,
        correctedAmount: detail.correctedAmount,
        cashierUID: detail.cashierUID,
        items: (detail.items || []).map(item => ({
          id: item.id,
          name: item.name || item.text,
          inStock: item.inStock,
          inStockOld: item.inStockOld,
          individualAmount: item.individualAmount,
          correctionReason: item.correctionReason,
          correctedIndividualAmount: item.correctedIndividualAmount,
        })),
      })))
    }
  }, [detail])

  useEffect(() => {
    if (userRole?.permissions?.deleteRevision || context.user.role === 'admin') {
      navigation.setOptions({
        headerRight: () => (
          detail != null && <HeaderIconButton
            onPress={async () => {
              if (!isLoading) {
                try {
                  setIsLoading(true)
                  await deleteRevision(db, detail)
                  removeItemFromData?.(sectionIndex, itemIndex)
                  setIsLoading(false)
                  navigation.goBack()
                } catch(error) {
                  console.log(error)
                  setSaveError(t('error'))
                  Sentry.captureException(error, { tags: { books_write_error: 'revision-delete' } })
                  // ATL-1799 — the delete did not happen; the button must work again, or the only
                  // way to retry is to leave the screen (the guard above ignores taps while loading).
                  setIsLoading(false)
                }
              }
            }}
            iconName={isLoading ? 'loader-outline' : 'trash-outline'}
            testID='detail-delete-button'
          />
        ),
      })
    }
  }, [isLoading])

  useEffect(() => {
    if (category && store && !detail) {
      let newData = []
      if (allProductsByCategoryDict[category.id]) { 
        for (let product of allProductsByCategoryDict[category.id]) {
          if (product.activeInStore?.[store]) {
            newData.push(addAdditionalFieldsToProductForRevision(product, store))
          }
        }
      }
      newData.sort((a, b) => a.name.localeCompare(b.name))
      setData(newData)
    }
  }, [category, store])

  useEffect(() => {
    if (productCategoriesList) {
      let newActiveProductCategoriesList = []
      for (let category of productCategoriesList) {
        if (allProductsByCategoryDict[category.id]?.length > 0) {
          newActiveProductCategoriesList.push(category)
        }
      }
      setActiveProductCategoriesList(newActiveProductCategoriesList)
    }
  }, [store, productCategoriesList.length])
  
  /**
   * ATL-1371 — «Исправленная сумма» has THREE cases and only two of them were ever distinguished.
   *
   * Empty means "no override": the key is removed, so the row falls back to its computed
   * `individualAmount` — which is what the header total already does, so the two agree.
   *
   * Unreadable means REFUSE, and getting that wrong twice in this file is the reason it is spelled
   * out here. `parseInt` used to coerce it (so '2.5' silently became 2), and the first version of
   * this fix SKIPPED it — which, on the correction path, silently DELETED a correction already in
   * the books: `newValue` is a spread of the saved item, the header recomputes from
   * `individualAmount`, `dailyStats.correctedRevisionsAmount` moves by the difference
   * (`inventory.ts:1313`), and the screen reports success while the unreadable text is still on
   * screen. Destroying a stored money value and calling it a save is worse than either thing it
   * replaced. The rest of this change refuses an amount nobody can read; so does this.
   *
   * @returns {{ ok: true, amounts: Array<number|null> } | { ok: false }}
   *   `amounts[i]` is the parsed override for row i, or null for "no override".
   */
  const readCorrectedAmounts = () => {
    const amounts = []
    for (const value of data) {
      const raw = value.correctedIndividualAmount
      const isBlank = raw === '' || raw === null || raw === undefined
      const parsed = isBlank ? null : parseDecimalAmount(raw)
      if (!isBlank && parsed === null) return { ok: false }
      amounts.push(parsed)
    }
    return { ok: true, amounts }
  }

  const save = async () => {
    // A message from an earlier attempt must not outlive it: the deleted-product message (ATL-1793)
    // tells the merchant to save again, and that save may well succeed.
    setSaveError('')
    const corrected = readCorrectedAmounts()
    if (!corrected.ok) {
      setSaveError(t('notANumberError'))
      setIsLoading(false)
      return
    }
    if (!detail) {
      setIsLoading(true)
      var newValues = []
      for (let index = 0; index < data.length; index++) {
        const value = data[index]
        let newValue = {
          // NOTE: deliberately NOT storing value.ref — the live native Firestore ref is
          // cyclical on iOS and crashes JSON.stringify. saveNewRevision resolves the product
          // ref from id via getFirestoreRef.
          name: value.name,
          id: value.id,
          inStock: value.inStock ? (value.type ? parseFloat(value.inStock) : parseInt(value.inStock)) : 0,
          inStockOld: value.inStockOld ? (value.type ? parseFloat(value.inStockOld) : parseInt(value.inStockOld)) : 0,
          individualAmount: calculateIndividualAmount(value),
        }
        if (value.correctionReason != null) {
          newValue.correctionReason = value.correctionReason
        }
        // ATL-1371 — see `readCorrectedAmounts`. An unreadable value never reaches here; null is
        // an EMPTY box, and an empty box means no override. `parseInt` used to store 2 for '2.5'.
        if (corrected.amounts[index] !== null) {
          newValue.correctedIndividualAmount = corrected.amounts[index]
        }
        if (newValue.inStockOld !== newValue.inStock) {
          newValues.push(newValue)
        }
      }
      if (newValues.length > 0) {
        let [amount, negativeAmount, positiveAmount] = calculateAmount()
        let info = {
          cashierUID: context.user.uid,
          productCategory: category,
          date: new Date(),
          dailyStatID: getDailyStatID(null, new Date()),
          items: newValues,
          amount,
          negativeAmount,
          positiveAmount,
          correctedAmount: calculateCorrectedAmount(),
          store,
          organizationId: context.user.organizationId
        }
        try {
          let ref = await saveNewRevision(db, info)
          addItemToData?.({...info, ref, id: ref.id}, info.date, info.store)
          console.log('[REVISION_DETAIL_DATA]', JSON.stringify(sanitizeForFirestore({
            id: ref.id,
            store: info.store,
            organizationId: context.user?.organizationId,
            // Scalar-map: the in-memory category carries a live Firestore ref (cyclical on
            // iOS) — stringifying it raw throws after a SUCCESSFUL save (Sentry 26A), which
            // the catch then surfaces as a false save-error. e2e contract is { id, name } | null.
            productCategory: info.productCategory
              ? { id: info.productCategory.id, name: info.productCategory.name }
              : null,
            amount: info.amount,
            negativeAmount: info.negativeAmount,
            positiveAmount: info.positiveAmount,
            correctedAmount: info.correctedAmount,
            cashierUID: info.cashierUID,
            items: (info.items || []).map(item => ({
              id: item.id,
              name: item.name || item.text,
              inStock: item.inStock,
              inStockOld: item.inStockOld,
              individualAmount: item.individualAmount,
            })),
          })));
          setSuccessMessageVisible(true)
          // ASO-RATING-2026-001 — a NEW stock count has been saved: a completed core action. Only
          // here, not in the correction path below, which edits a revision that was already
          // completed. After the success message, so it can never be read as part of a failure.
          void requestAppReviewAtMilestone(APP_REVIEW_TRIGGERS.REVISION_COMPLETED, postHog)
        } catch (error) {
          Sentry.captureException(error, { tags: { books_write_error: 'revision-create' } })
          console.log(error)
          // ATL-1793 — the count was refused because these products no longer exist in Firestore
          // (deleted there, still listed from Supabase). Retrying can never succeed, so name them;
          // setting a row back to its recorded quantity drops it from `newValues` above.
          setSaveError(isProductDocumentsMissingError(error)
            ? t('revisions.deletedProductsError', {
              products: error.products.map(product => `«${String(product.name).trim()}»`).join(', '),
            })
            : saveFailureText(error, t, t('error')))
        }
      }
      setIsLoading(false)
    } else {
      setIsLoading(true)
      var newItems = []
      for (let index = 0; index < data.length; index++) {
        let value = data[index]
        let newValue = {...detail.items[index]}
        // Drop the live native Firestore ref — it's cyclical on iOS and crashes JSON.stringify
        // when this item flows into the update payload / dual-write (Sentry 26A/26B, RevisionDetail).
        delete newValue.ref
        if (value.correctionReason != null) {
          newValue.correctionReason = value.correctionReason
        }
        // ATL-1371 — the `else` is LOAD-BEARING here in a way it is not on the create path:
        // `newValue` is a spread of `detail.items[index]`, the PREVIOUSLY SAVED item, while the
        // editor writes into a copy of `data`. Without it, emptying the box left the old number in
        // the item while `calculateCorrectedAmount()` fell through to the computed value for that
        // same row — the header and its items disagreeing permanently, in both stores, with the
        // backend report preferring the item (`revisions.service.ts:146`).
        //
        // Reaching this with an UNREADABLE value is impossible now (`readCorrectedAmounts` refuses
        // the whole save), which is the point: `delete` here means "the merchant cleared the box",
        // and clearing an override is a thing they are allowed to do.
        if (corrected.amounts[index] !== null) {
          newValue.correctedIndividualAmount = corrected.amounts[index]
        } else {
          delete newValue.correctedIndividualAmount
        }
        newItems.push(newValue)
      }
      try {
        let updatedData = {
          items: newItems, 
          correctedAmount: calculateCorrectedAmount(), 
        }
        await updateRevision(db, updatedData, detail)
        editItemInData?.({...detail, ...updatedData}, sectionIndex, itemIndex)
        setSuccessMessageVisible(true)
      } catch (error) {
        Sentry.captureException(error, { tags: { books_write_error: 'revision-correction-update' } })
        console.log(error)
        setSaveError(t('error'))
      }
      setIsLoading(false)
    }
    
  }

  const fields = [
    {
      key: 'name',
      type: 'text',
      columnStyle: 'nameColumn',
      textCategory: 'p1'
    },
    {
      key: 'inStockOld',
      type: 'text',
      title: t('revisions.quantity'),
      caption: item => (productUnitsDict[item.productUnit]?.shortName || t('pcs')),
      formatter: value => (value ?? 0).toString()
    },
    {
      key: 'inStock',
      type: detail ? 'text' : 'input',
      title: t('revisions.quantityFact'),
      keyboardType: 'decimal-pad',
      onChange: (index, text) => {
        const newProductInfo = {...data[index], inStock: text.replace(/,/g, '.')}
        const newData = [...data]
        newData.splice(index, 1, newProductInfo)
        setData(newData)
      },
      caption: item => (productUnitsDict[item.productUnit]?.shortName || t('pcs')),
      formatter: value => (value ?? 0).toString()
    },
    {
      key: 'individualAmount',
      type: 'text',
      title: t('revisions.amount'),
      formatter: (value, item) => currencyFormatter.format(detail != null ? item.individualAmount : calculateIndividualAmount(item))
    }
  ]

  // Add correction fields if viewing detail with permissions
  if (detail != null && (userRole?.permissions?.deleteRevision || context.user.role === 'admin')) {
    fields.push({
      key: 'correctionReason',
      type: 'select',
      title: t('revisions.reason'),
      options: localizedCorrectionReasons,
      undisable: true,
      onChange: (index, itemValue) => {
        const newProductInfo = {...data[index], correctionReason: itemValue}
        const newData = [...data]
        newData.splice(index, 1, newProductInfo)
        setData(newData)
      }
    })
    
    fields.push({
      key: 'correctedIndividualAmount',
      // ATL-1371 — «Исправленная сумма» is MONEY (`calculateIndividualAmount` is
      // `purchasePrice * stock delta`), and `number-pad` is the digits-only keypad: the merchant
      // could not type a separator at all, while this field's own `onChange` already rewrites
      // ',' → '.', i.e. decimals were always intended. `AdaptiveTableCard` takes a keyboardType
      // STRING rather than a component, so this one cannot go through `MoneyTextInput`.
      type: 'input',
      title: t('revisions.correctedAmount'),
      keyboardType: 'decimal-pad',
      undisable: true,
      onChange: (index, text) => {
        const newProductInfo = {...data[index], correctedIndividualAmount: text.replace(/,/g, '.')}
        const newData = [...data]
        newData.splice(index, 1, newProductInfo)
        setData(newData)
      }
    })
  }

  const renderFooter = () => {
    if (category && store) {
      return (
        <View>
          <Text category='label' style={styles.label}>
            {t('revisions.total')}
          </Text>
          <Text category='label' style={styles.amount}>
            {detail != null ? currencyFormatter.format(detail.amount) : currencyFormatter.format(calculateAmount()?.[0])}
          </Text>
          { detail != null && (
            <>
              <Text category='label' style={styles.label}>
                {t('revisions.totalWithCorrections')}
              </Text>
              <Text category='label' style={styles.amount}>
                {currencyFormatter.format(calculateCorrectedAmount())}
              </Text>
            </>
          )}
          {saveError != '' && (
            <Text category='p1' status='danger' style={styles.label}>
              {saveError}
            </Text>
          )}
          {successMessageVisible && (
            <>
            <Text status='success'>{t('success')}</Text>
            <Button onPress={() => navigation.goBack()} style={styles.submitButton}>
              {t('back')}
            </Button>
            </>
          )}
          {isLoading ? (
            <View style={styles.spinner}><Spinner /></View>
          ) : (
            !successMessageVisible && <Button onPress={save} style={styles.submitButton}>
              {t('save')}
            </Button>
          )}
        </View>
      )
    }
    return null
  }

  const calculateAmount = () => {
    var amount = 0
    var negativeAmount = 0
    var positiveAmount = 0
    if (data) {
      for (let value of data) {
        const newValue = {
          inStock: value.inStock ? (value.type ? parseFloat(value.inStock) : parseInt(value.inStock)) : 0,
          inStockOld: value.inStockOld ? (value.type ? parseFloat(value.inStockOld) : parseInt(value.inStockOld)) : 0,
        }
        if (newValue.inStockOld != newValue.inStock) {
          let individualAmount = (value.purchasePrice || 0) * (newValue.inStock-newValue.inStockOld)
          amount += individualAmount
          if (individualAmount < 0) {
            negativeAmount += individualAmount
          } else {
            positiveAmount += individualAmount
          }
        }
      }
    }
    return [amount, negativeAmount, positiveAmount]
  }

  const calculateCorrectedAmount = () => {
    var amount = 0
    if (data) {
      for (let value of data) {
        // ATL-1371 — the DISPLAYED total reads the box the same way the save does: an empty or
        // unreadable override falls through to the computed amount. `parseInt` made this total NaN
        // for an unreadable value, which rendered as a NaN currency. (The save refuses such a
        // value outright, so this branch is what the merchant sees while they are still typing.)
        const correctedAmount = parseDecimalAmount(value.correctedIndividualAmount)
        if (correctedAmount !== null) {
          amount += correctedAmount
        } else {
          if (value.individualAmount) {
            amount += value.individualAmount
          } else {
            const newValue = {
              inStock: value.inStock ? (value.type ? parseFloat(value.inStock) : parseInt(value.inStock)) : 0,
              inStockOld: value.inStockOld ? (value.type ? parseFloat(value.inStockOld) : parseInt(value.inStockOld)) : 0,
            }
            if (newValue.inStockOld != newValue.inStock) {
              amount += (value.purchasePrice || 0) * (newValue.inStock-newValue.inStockOld)
            }
          }
        }
      }
    }
    return amount
  }

  const calculateIndividualAmount = (value) => {
    var amount = 0
    const newValue = {
      inStock: value.inStock ? (value.type ? parseFloat(value.inStock) : parseInt(value.inStock)) : 0,
      inStockOld: value.inStockOld ? (value.type ? parseFloat(value.inStockOld) : parseInt(value.inStockOld)) : 0,
    }
    if (newValue.inStockOld != newValue.inStock) {
      amount = (value.purchasePrice || 0) * (newValue.inStock-newValue.inStockOld)
    }
    return amount
  }
  
  return (
    <KeyboardAwareScrollView style={styles.container} keyboardShouldPersistTaps='handled' keyboardDismissMode='none' extraHeight={100}>
      <View style={{paddingHorizontal: 20}}>
        {detail && <Text appearance='hint' style={{textAlign: 'center', marginVertical: 10}}>
          {detail.id}
        </Text>}
        { Object.values(storesDict).length > 1 && (userRole?.permissions?.['viewOtherStores'] || context.user.role === 'admin') && (
          <>
            <Text category='label' style={styles.label}>
              {t('store')}
            </Text>
            <PlatformDependantSelect
              data={Object.values(storesDict)}
              selectedOption={storesDict?.[store]}
              disabled={(detail != null)}
              onSelect={(itemValue, itemIndex) => {
                setStore(itemValue.id)
              }}
            />
          </>
        )}
        <Text category='label' style={styles.label}>
          {t('revisions.category')}
        </Text>
        <PlatformDependantSelect
          data={activeProductCategoriesList}
          style={styles.select}
          selectedOption={category}
          onSelect={(itemValue) => {
            setCategory(itemValue)
          }}
          disabled={(detail != null)}
        />
      </View>
      <AdaptiveTableCard
        data={data}
        fields={fields}
        disabled={detail != null}
        breakpoint={(detail != null && (userRole?.permissions?.deleteRevision || context.user.role === 'admin')) ? 900 : 700}
        renderFooter={renderFooter}
        styles={{list: {paddingHorizontal: 20}}}
      />
      <View style={{height: 200}}></View>
    </KeyboardAwareScrollView>
  )
}
  
  const styles = StyleSheet.create({
    container: {
      flex: 1, 
    },
    select: {
      // marginHorizontal: 20,
      marginBottom: 10,
    },
    label: {
      // marginHorizontal: 20,
      marginTop: 10,
      marginBottom: 5,
    },
    amount: {
      // marginHorizontal: 20,
      marginBottom: 10,
    },
    submitButton: {
      marginVertical: 20,
    },
    spinner: {
      marginTop: 15,
      alignItems: 'center'
    },
  })